import { useEffect, useState, type FormEvent } from 'react';
// use plain anchor for logo to avoid requiring router context in tests
import { AlertCircle, ArrowRight, Lock, ShieldCheck } from 'lucide-react';
import { getBranding } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import { getAppLogoSrc, getAppLogoSizeStyle } from '../../constants/branding';
import { exchangeCodeForSessionFromUrl, getCurrentSession, signOut, updatePassword } from '../../lib/auth.ts';

type ResetPasswordPageProps = {
  onBackToLogin?: () => void;
};

const navigateTo = (nextPath: string) => {
  try {
    window.history.replaceState({}, '', nextPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  } catch {
    try {
      window.location.href = nextPath;
    } catch {}
  }
};

export default function ResetPasswordPage({ onBackToLogin }: ResetPasswordPageProps) {
  const logoSrc = getAppLogoSrc({ variant: 'horizontalSplit', theme: 'dark' });
  const logoStyle = getAppLogoSizeStyle({ variant: 'horizontalSplit' });
  const brand = getBranding(currentRuntime.brand.id);
  const title = brand?.appName || brand?.companyName || 'your workspace';

  const [isChecking, setIsChecking] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const session = await exchangeCodeForSessionFromUrl();
        if (cancelled) return;

        if (session?.user) {
          try {
            window.history.replaceState({}, '', '/auth/reset-password');
          } catch {}
          setIsReady(true);
          return;
        }

        const currentSession = await getCurrentSession();
        if (cancelled) return;

        if (currentSession?.user) {
          setIsReady(true);
          return;
        }

        setError('This password reset link is invalid or expired.');
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('This password reset link is invalid or expired.');
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!password || password.length < 8) {
      setError('Please choose a password with at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      await updatePassword(password);
      await signOut({ scope: 'local' }).catch(() => undefined);
      navigateTo('/login?reset=success');
    } catch (err) {
      console.error(err);
      const authError = err as Error;
      setError(authError?.message || 'Unable to update your password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] p-4 font-sans text-slate-200">
      <div className="absolute top-0 left-0 h-full w-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl opacity-30" />
        <div className="absolute bottom-[-10%] right-[-10%] h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl opacity-30" />
      </div>

      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        {onBackToLogin ? (
          <div className="mb-5 w-full text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center justify-center text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 transition hover:text-slate-200"
            >
              <span className="border-b border-sky-300/70 pb-1">Back to Sign In</span>
            </button>
          </div>
        ) : null}

        <div className="w-full overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/85 shadow-2xl backdrop-blur-xl">
          <div className="h-1 w-full bg-gradient-to-r from-blue-800 via-green-200 to-green-600" />

          <div className="p-5">
            <div className="text-center mb-6">
              <a href="/" aria-label="Open landing page" className="mx-auto mb-1 block w-auto">
                <img
                  src={logoSrc}
                  alt={title}
                  className="mx-auto mb-1 block w-auto object-contain drop-shadow-lg"
                  style={logoStyle}
                />
              </a>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-200">
                <ShieldCheck className="h-3.5 w-3.5" />
                Password recovery
              </div>
              <h1 className="mt-5 text-2xl font-semibold tracking-tight text-slate-100">Set a new password</h1>
              <p className="mt-3 text-sm text-slate-400">
                Create a new password for your {title} account. This page stays outside the workspace and does not trigger tenant access checks.
              </p>
            </div>

            {isChecking ? (
              <div className="mb-4 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3 text-sm text-blue-100" aria-live="polite">
                Verifying your reset link...
              </div>
            ) : null}

            {error ? (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-200" aria-live="polite">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            {!isChecking && isReady ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="ml-1 text-xs font-semibold uppercase tracking-wider text-slate-300">New Password</label>
                  <div className="group relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                      <Lock className="h-5 w-5" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-3 text-slate-200 outline-none transition-all duration-200 placeholder-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                      placeholder="New password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="ml-1 text-xs font-semibold uppercase tracking-wider text-slate-300">Confirm Password</label>
                  <div className="group relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                      <Lock className="h-5 w-5" />
                    </div>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      className="block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-3 text-slate-200 outline-none transition-all duration-200 placeholder-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-400 px-4 py-3 font-semibold text-white shadow-lg shadow-blue-900/30 transition-all duration-200 hover:scale-[1.02] hover:from-blue-500 hover:to-blue-300 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      Update password <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
